import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Driver } from '../data/mockData';
import { ChevronLeft, CheckCircle2 } from 'lucide-react';
export interface RouteSuggestion {
  id: number;
  label: string;
  roads: string;
  etaImpactMin: number;
  etaImpact: string;
  etaColor: string;
  distanceDiff: string;
  note: string;
}
interface SuggestRoutePanelProps {
  driver: Driver;
  onClose: () => void;
  onApply?: (route: any) => void;
  onHoverRoute?: (routeId: number | null) => void;
  customSuggestions?: RouteSuggestion[];
}
export function SuggestRoutePanel({
  driver,
  onClose,
  onApply,
  onHoverRoute,
  customSuggestions
}: SuggestRoutePanelProps) {
  const [selectedRoute, setSelectedRoute] = useState<number | null>(null);
  const nextStop = driver.route.find(s => s.status !== 'completed');
  // Compute a deterministic distance for the current route
  const currentDistance = (() => {
    if (nextStop?.distance) return nextStop.distance;
    const idx = driver.route.indexOf(nextStop!);
    const base = (driver.id.charCodeAt(driver.id.length - 1) * 7 + (idx >= 0 ? idx : 0) * 11) % 20 + 12;
    return `${base} mi`;
  })();
  // Compute base ETA minutes from next stop
  const baseEtaMin = (() => {
    if (!nextStop) return 0;
    const [h, m] = nextStop.eta.replace('~', '').split(':').map(Number);
    return h * 60 + m;
  })();
  const formatEta = (minutes: number) => {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  };
  // Deterministic mock data
  const suggestions = customSuggestions || [{
    id: 1,
    label: 'Via I-64 W',
    roads: 'I-64 W → VA-288 S → Hull Street Rd',
    etaImpactMin: -12,
    etaImpact: '-12 min',
    etaColor: 'text-brand-green',
    distanceDiff: '-2.4 mi',
    note: 'Fastest route'
  }, {
    id: 2,
    label: 'Via US-360 E',
    roads: 'US-360 E → State Route 288',
    etaImpactMin: 5,
    etaImpact: '+5 min',
    etaColor: 'text-brand-amber',
    distanceDiff: '+1.2 mi',
    note: 'Avoids construction on I-95'
  }];
  return <motion.div initial={{
    x: '100%'
  }} animate={{
    x: 0
  }} exit={{
    x: '100%'
  }} transition={{
    type: 'spring',
    damping: 25,
    stiffness: 250
  }} className="absolute inset-0 bg-slate-950/95 backdrop-blur-md border-l border-slate-700 flex flex-col z-50 shadow-2xl" data-id="element-594">
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-2 border-b border-slate-800 shrink-0 bg-slate-900/50" data-id="element-595">
        <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-slate-800 text-slate-400 hover:text-slate-200 transition-colors -ml-2" data-id="element-596">
          <ChevronLeft className="w-5 h-5" data-id="element-597" />
        </button>
        <h3 className="text-sm font-semibold text-slate-100" data-id="element-598">Reroute</h3>
        <span className="text-[10px] font-mono font-bold px-2 py-1 rounded bg-slate-800 text-slate-300 ml-auto" data-id="element-599">
          {driver.truckId}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 scrollbar-hide space-y-6" data-id="element-600">
        {/* Current Route */}
        <div data-id="element-601">
          <div className="flex items-center justify-between mb-1" data-id="element-602">
            <h4 className="text-xs font-medium text-slate-400 uppercase tracking-wider" data-id="element-603">
              Current Route
            </h4>
          </div>
          <div className="flex items-baseline justify-between" data-id="element-604">
            <p className="text-sm font-medium text-slate-200" data-id="element-605">Via I-95 N</p>
            <div className="flex items-center gap-3" data-id="element-606">
              <span className="text-xs font-mono text-slate-400" data-id="element-607">
                {currentDistance}
              </span>
              <span className="text-xs font-mono text-slate-300" data-id="element-608">
                ETA {nextStop?.eta || '--:--'}
              </span>
            </div>
          </div>
          <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-609">
            I-95 N → US-301 S → Broad St
          </p>
          <p className="text-[11px] text-slate-500 mt-0.5" data-id="element-610">
            → {nextStop?.address || 'No active stops'}
          </p>
        </div>

        <div className="h-px bg-slate-800" data-id="element-611" />

        {/* Alternate Routes */}
        <div data-id="element-612">
          <h4 className="text-xs font-medium text-slate-400 mb-3 uppercase tracking-wider" data-id="element-613">
            Alternate Routes
          </h4>
          <div className="space-y-3" data-id="element-614">
            {suggestions.map(route => {
            const isSelected = selectedRoute === route.id;
            const newEta = formatEta(baseEtaMin + route.etaImpactMin);
            return <div key={route.id} onClick={() => setSelectedRoute(route.id)} onMouseEnter={() => onHoverRoute?.(route.id)} onMouseLeave={() => onHoverRoute?.(null)} className={`border rounded-xl p-3 cursor-pointer transition-all ${isSelected ? 'bg-brand-blue/10 border-brand-blue shadow-[0_0_15px_rgba(59,130,246,0.15)]' : 'bg-slate-900/50 border-slate-800 hover:border-slate-600'}`} data-id="element-615">
                  <div className="flex items-start justify-between mb-2" data-id="element-616">
                    <div className="flex flex-col gap-2" data-id="element-617">
                      <h5 className={`text-sm font-semibold ${isSelected ? 'text-brand-blue' : 'text-slate-200'}`} data-id="element-618">
                        {route.label}
                      </h5>
                      <div data-id="element-619">
                        <p className="text-[11px] text-slate-400 mb-1.5" data-id="element-620">
                          {route.roads}
                        </p>
                        <div className="flex items-center gap-3 text-[10px]" data-id="element-621">
                          <span className="text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded" data-id="element-622">
                            {route.distanceDiff}
                          </span>
                          <span className="text-slate-500 italic" data-id="element-623">
                            {route.note}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right" data-id="element-624">
                      <p className="text-xs font-mono text-slate-200" data-id="element-625">
                        ETA {newEta}
                      </p>
                      <span className={`text-[10px] font-bold ${route.etaColor}`} data-id="element-626">
                        {route.etaImpact}
                      </span>
                    </div>
                  </div>
                </div>;
          })}
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="p-4 border-t border-slate-800 bg-slate-900/95 shrink-0 flex gap-3" data-id="element-627">
        <button onClick={onClose} className="flex-1 py-2 text-xs font-semibold rounded-lg transition-colors bg-slate-800 hover:bg-slate-700 text-slate-200" data-id="element-628">
          Cancel
        </button>
        <button onClick={() => {
        if (selectedRoute && onApply) {
          onApply(suggestions.find(s => s.id === selectedRoute));
        }
      }} disabled={!selectedRoute} className={`flex-1 py-2 text-xs font-semibold rounded-lg transition-colors ${selectedRoute ? 'bg-brand-blue hover:bg-brand-blue/90 text-white' : 'bg-slate-800/50 text-slate-500 cursor-default'}`} data-id="element-629">
          Update Route
        </button>
      </div>
    </motion.div>;
}